import React, { useState, useContext } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { updateProfile, resetPassword } from '../../services/api.js';
import ThemeContext from '../../ThemeContext';

const Account = ({ currentUser, setCurrentUser, setError }) => {
  const { theme } = useContext(ThemeContext);

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 18;
  const labelFontSize = Math.max(11, bodyFontSize - 2);
  const helperFontSize = Math.max(10, bodyFontSize - 3);

  const [formData, setFormData] = useState({
    first_name: currentUser?.name?.split(' ')[0] || '',
    last_name: currentUser?.name?.split(' ').slice(1).join(' ') || '',
    email: currentUser?.email || '',
    username: currentUser?.username || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });

  const [errors, setErrors] = useState({});
  const [realTimeErrors, setRealTimeErrors] = useState({});
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);
  const [touched, setTouched] = useState({
    current_password: false,
    new_password: false,
    new_password_confirmation: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setRealTimeErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validatePasswordComplexity = (password, isBlur = false) => {
    if (!password && (isNewPasswordFocused || isBlur)) {
      return 'Please fill this field.';
    }
    if (password.length < 8 && (isNewPasswordFocused || isBlur)) {
      return 'Password must be at least 8 characters.';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter.';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter.';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number.';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character.';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setRealTimeErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'new_password') {
      const complexityError = isNewPasswordFocused
        ? validatePasswordComplexity(value)
        : '';
      setRealTimeErrors((prev) => ({
        ...prev,
        new_password: complexityError,
      }));

      if (
        passwordData.new_password_confirmation &&
        value !== passwordData.new_password_confirmation
      ) {
        setRealTimeErrors((prev) => ({
          ...prev,
          new_password_confirmation: 'Passwords do not match.',
        }));
      } else {
        setRealTimeErrors((prev) => ({
          ...prev,
          new_password_confirmation: '',
        }));
      }
    }

    if (name === 'new_password_confirmation' && value) {
      if (value !== passwordData.new_password) {
        setRealTimeErrors((prev) => ({
          ...prev,
          new_password_confirmation: 'Passwords do not match.',
        }));
      } else {
        setRealTimeErrors((prev) => ({
          ...prev,
          new_password_confirmation: '',
        }));
      }
    }
  };

  const handleNewPasswordFocus = () => {
    setIsNewPasswordFocused(true);
    const complexityError = validatePasswordComplexity(passwordData.new_password);
    setRealTimeErrors((prev) => ({
      ...prev,
      new_password: complexityError,
    }));
  };

  const handleNewPasswordBlur = () => {
    setIsNewPasswordFocused(false);
    setTouched((prev) => ({ ...prev, new_password: true }));
    const complexityError = validatePasswordComplexity(
      passwordData.new_password,
      true
    );
    setRealTimeErrors((prev) => ({
      ...prev,
      new_password: complexityError,
    }));
  };

  const handleNewPasswordConfirmationBlur = () => {
    setTouched((prev) => ({ ...prev, new_password_confirmation: true }));
    if (!passwordData.new_password_confirmation) {
      setRealTimeErrors((prev) => ({
        ...prev,
        new_password_confirmation: 'Please fill this field.',
      }));
    } else if (
      passwordData.new_password_confirmation !== passwordData.new_password
    ) {
      setRealTimeErrors((prev) => ({
        ...prev,
        new_password_confirmation: 'Passwords do not match.',
      }));
    }
  };

  const validateForm = () => {
    const requiredFields = ['first_name', 'last_name'];
    const newErrors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = 'Please fill this field.';
        isValid = false;
      }
    });

    if (
      passwordData.current_password ||
      passwordData.new_password ||
      passwordData.new_password_confirmation
    ) {
      if (!passwordData.current_password) {
        newErrors.current_password = 'Please fill this field.';
        isValid = false;
      }
      if (!passwordData.new_password) {
        newErrors.new_password = 'Please fill this field.';
        isValid = false;
      } else {
        const complexityError = validatePasswordComplexity(
          passwordData.new_password,
          true
        );
        if (complexityError) {
          newErrors.new_password = complexityError;
          isValid = false;
        }
      }
      if (!passwordData.new_password_confirmation) {
        newErrors.new_password_confirmation = 'Please fill this field.';
        isValid = false;
      } else if (
        passwordData.new_password !== passwordData.new_password_confirmation
      ) {
        newErrors.new_password_confirmation = 'Passwords do not match.';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAccountUpdate = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form.');
      return;
    }

    try {
      // Update profile name
      const profileData = {
        name: `${formData.first_name} ${formData.last_name}`.trim(),
      };
      await updateProfile(profileData);

      // Update password if all fields are filled
      if (
        passwordData.current_password &&
        passwordData.new_password &&
        passwordData.new_password_confirmation
      ) {
        await resetPassword({
          old_password: passwordData.current_password,
          new_password: passwordData.new_password,
          new_password_confirmation: passwordData.new_password_confirmation,
        });
      }

      // Update local state and localStorage
      const updatedUser = {
        ...currentUser,
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        email: formData.email,
        username: formData.username,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (typeof setCurrentUser === 'function') {
  setCurrentUser(updatedUser);
}

      // Reset password fields
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });

      // Clear errors
      setErrors({});
      setRealTimeErrors({});
      setTouched({
        current_password: false,
        new_password: false,
        new_password_confirmation: false,
      });
      setError('');

      // SUCCESS TOAST — This will now always show on successful update
      toast.success('Account updated successfully.');

    } catch (err) {
      console.error('Account update failed:', err);

      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to update account. Please try again.';

      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: currentUser?.name?.split(' ')[0] || '',
      last_name: currentUser?.name?.split(' ').slice(1).join(' ') || '',
      email: currentUser?.email || '',
      username: currentUser?.username || '',
    });
    setPasswordData({
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
    });
    setErrors({});
    setRealTimeErrors({});
    setTouched({
      current_password: false,
      new_password: false,
      new_password_confirmation: false,
    });
    setError('');
    setIsNewPasswordFocused(false);
  };

  return (
    <>
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1rem",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop: "10px",
        }}
      >
        <div
          style={{
            fontFamily,
            fontSize: `${bodyFontSize}px`,
            maxWidth: '100%',
            margin: '0 auto',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            width: '100%',
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
          }}
        >
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            toastStyle={{
              backgroundColor: theme.colors.bgPrimary,
              color: theme.colors.textPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }}
          />

          <h4
            style={{
              fontSize: `${titleFontSize}px`,
              fontWeight: 'bold',
              marginTop: '0.75rem',
              marginBottom: '1rem',
              color: theme.colors.textPrimary,
              textAlign: 'left',
            }}
          >
            My Account
          </h4>

          <div
            style={{
              padding: '1rem',
              borderRadius: '10px',
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              width: '100%',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '200px',
                    maxWidth: '300px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.first_name || realTimeErrors.first_name
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgPrimary,
                      color: theme.colors.textPrimary,
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.first_name || realTimeErrors.first_name) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.first_name || realTimeErrors.first_name}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '200px',
                    maxWidth: '300px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    Last Name*
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.last_name || realTimeErrors.last_name
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgPrimary,
                      color: theme.colors.textPrimary,
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.last_name || realTimeErrors.last_name) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.last_name || realTimeErrors.last_name}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '300px',
                    maxWidth: '450px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    Email*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.email || realTimeErrors.email
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgSecondary,
                      color: theme.colors.textPrimary,
                      cursor: 'not-allowed',
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.email || realTimeErrors.email) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.email || realTimeErrors.email}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '300px',
                    maxWidth: '450px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    Username*
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    disabled
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.username || realTimeErrors.username
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgSecondary,
                      color: theme.colors.textPrimary,
                      cursor: 'not-allowed',
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.username || realTimeErrors.username) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.username || realTimeErrors.username}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '300px',
                    maxWidth: '450px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    onBlur={() =>
                      setTouched((prev) => ({
                        ...prev,
                        current_password: true,
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.current_password ||
                        realTimeErrors.current_password
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgPrimary,
                      color: theme.colors.textPrimary,
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.current_password ||
                    realTimeErrors.current_password) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.current_password ||
                        realTimeErrors.current_password}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '300px',
                    maxWidth: '450px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    onFocus={handleNewPasswordFocus}
                    onBlur={handleNewPasswordBlur}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.new_password ||
                        realTimeErrors.new_password
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgPrimary,
                      color: theme.colors.textPrimary,
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.new_password ||
                    realTimeErrors.new_password) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.new_password ||
                        realTimeErrors.new_password}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <div
                  style={{
                    flex: '1 1 200px',
                    minWidth: '300px',
                    maxWidth: '450px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.4rem',
                      fontSize: `${labelFontSize}px`,
                    }}
                  >
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="new_password_confirmation"
                    value={passwordData.new_password_confirmation}
                    onChange={handlePasswordChange}
                    onBlur={handleNewPasswordConfirmationBlur}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      fontSize: `${bodyFontSize - 2}px`,
                      borderRadius: '4px',
                      border: `1px solid ${
                        errors.new_password_confirmation ||
                        realTimeErrors.new_password_confirmation
                          ? '#dc3545'
                          : theme.colors.borderColor
                      }`,
                      outline: 'none',
                      backgroundColor: theme.colors.bgPrimary,
                      color: theme.colors.textPrimary,
                      boxSizing: 'border-box',
                      fontFamily,
                    }}
                  />
                  {(errors.new_password_confirmation ||
                    realTimeErrors.new_password_confirmation) && (
                    <span
                      style={{
                        color: '#dc3545',
                        fontSize: `${helperFontSize}px`,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.new_password_confirmation ||
                        realTimeErrors.new_password_confirmation}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '0.5rem 1.25rem',
                    fontSize: `${bodyFontSize - 2}px`,
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.gradients.secondary,
                    color: theme.colors.textWhite,
                    cursor: 'pointer',
                    minWidth: '120px',
                    fontFamily,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleAccountUpdate}
                  style={{
                    padding: '0.5rem 1.25rem',
                    fontSize: `${bodyFontSize - 2}px`,
                    borderRadius: '6px',
                    border: 'none',
                    background: theme.gradients.primary,
                    color: theme.colors.textWhite,
                    cursor: 'pointer',
                    minWidth: '120px',
                    fontFamily,
                  }}
                >
                  Save Changes
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Account;